import objectHash from "object-hash";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { debounceTime, Subject } from "rxjs";
import type {
  PivotColumnResponse,
  PivotGeneralSettings,
  ReportField,
} from "../../../../../../../../../api/types/svTypes.ts";
import { selectRefreshToken } from "../../../../../store/selectors/currentReportSelectors";
import {
  selectColumns,
  selectConditions,
  selectPivotSettingsState,
  selectRows,
  selectSorts,
  selectValues,
} from "../../../../../store/selectors/fieldsStateSelectors";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors";
import { getAggregationBasedFields } from "../../../../../utilities/fieldsHelper.ts";
import { getAllFilters } from "../../../../../utilities/getAllConditions.ts";
import { isPivotConfigurationValid } from "../../../../../utilities/isConfigurationValid.ts";
import type { PivotGridState } from "../../../../viewer/pivot/hooks/gridState.types.ts";
import getTrackedMeasureProperties from "../../../utils/getTrackedMeasureProperties.ts";
import useDataLoadingBuilder from "./useDataLoadingBuilder.ts";

type PivotDataSettings = Omit<PivotGeneralSettings, "detailedView" | "showGroupedColumns" | "hideGroupRows">;

export default function useGridStateBuilder(): PivotGridState {
  const subjectDelay = useMemo(() => new Subject<string>(), []);
  const dimensions = useSelector(selectDimensions);
  const [columns, setColumns] = useState<PivotColumnResponse[]>([]);

  const conditionsAreaValues = useSelector(selectConditions);
  const valuesAreaValues = useSelector(selectValues);
  const rowsAreaValues = useSelector(selectRows);
  const columnsAreaValues = useSelector(selectColumns);
  const sortAreaValues = useSelector(selectSorts);
  const settingsAreaValues = useSelector(selectPivotSettingsState);
  const { calculate, error, calculating, pivot, failedMeasures, handleInvalidConfiguration } = useDataLoadingBuilder();
  const refreshToken = useSelector(selectRefreshToken);
  const calculatedFields = useMemo(
    () => valuesAreaValues.concat(getAggregationBasedFields(rowsAreaValues.concat(columnsAreaValues))),
    [columnsAreaValues, rowsAreaValues, valuesAreaValues]
  );
  const measuresHash = useMemo(() => objectHash(getTrackedMeasureProperties(calculatedFields)), [calculatedFields]);

  const conditionsFiltersHash = useMemo(
    () => objectHash(getAllFilters(conditionsAreaValues, calculatedFields)),
    [calculatedFields, conditionsAreaValues]
  );

  const rowsAreaHash = useMemo(() => rowFieldHash(rowsAreaValues), [rowsAreaValues]);
  const columnsAreaHash = useMemo(() => generalFieldHash(columnsAreaValues), [columnsAreaValues]);

  const settingsHash = useMemo(
    () =>
      objectHash({
        hideBlankColumns: settingsAreaValues.hideBlankColumns,
        hideBlankColumnsScope: settingsAreaValues.hideBlankColumnsScope,
        hideBlankColumnIds: settingsAreaValues.hideBlankColumnIds,
        hideZeroColumns: settingsAreaValues.hideZeroColumns,
        hideBlankRows: settingsAreaValues.hideBlankRows,
        hideZeroRows: settingsAreaValues.hideZeroRows,
        rowsGrandTotal: settingsAreaValues.rowsGrandTotal,
      } satisfies PivotDataSettings),
    [settingsAreaValues]
  );

  const configurationValidity = useMemo(() => {
    const fields = rowsAreaValues.concat(columnsAreaValues).concat(rowsAreaValues);
    return isPivotConfigurationValid(conditionsAreaValues, fields, valuesAreaValues, dimensions);
  }, [columnsAreaValues, conditionsAreaValues, dimensions, rowsAreaValues, valuesAreaValues]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(100)).subscribe(calculate);
    return () => subscription.unsubscribe();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configurationValidity.valid) {
      subjectDelay.next(refreshToken);
    } else {
      handleInvalidConfiguration();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    conditionsFiltersHash,
    rowsAreaHash,
    columnsAreaHash,
    measuresHash,
    refreshToken,
    settingsHash,
    sortAreaValues,
    configurationValidity.valid,
  ]);

  useEffect(() => {
    if (!pivot) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setColumns([]);
    } else {
      const gridColumns = pivot.columns;
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setColumns(gridColumns);
    }
  }, [pivot]);

  return { pivot, columns, error, calculating, configurationValidity, failedMeasures };
}

function rowFieldHash(fields: ReportField[]) {
  return objectHash(
    fields.map((v) => ({
      n: v.meta.name,
      g: v.config.grouping,
      df: v.config.displayValues,
      showTotals: v.config.style?.showTotals,
      showLogoIcon: v.config.showLogoIcon,
      customLabel: v.config.customLabel,
      hideBlankValues: v.config.hideBlankValues,
      hideZeroValues: v.config.hideZeroValues,
    }))
  );
}

export function generalFieldHash(fields: ReportField[]) {
  return objectHash(
    fields.map((v) => ({
      n: v.meta.name,
      g: v.config.grouping,
      df: v.config.displayValues,
      showTotals: v.config.style?.showTotals,
      customLabel: v.config.customLabel,
      hideBlankValues: v.config.hideBlankValues,
      hideZeroValues: v.config.hideZeroValues,
    }))
  );
}
